import axios from 'axios';
import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const API_URL = process.env.REACT_APP_SERVER_URL;
const API_KEY = process.env.REACT_APP_UMMAHFY_API_KEY;

/**
 * Analytics Service - Handles tracking events for both Mixpanel and internal recommendation system
 */
class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.userId = null;
    this.userProperties = {};
    this.queuedEvents = [];
    this.isBot = this.detectBot();
  }

  /**
   * Detect if the current user is a bot or crawler
   * @returns {boolean} True if user agent appears to be a bot
   */
  detectBot() {
    if (typeof window === 'undefined' || !window.navigator) {
      return false;
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    
    // Common bot/crawler user agent patterns
    const botPatterns = [
      'bot', 'spider', 'crawl', 'slurp', 'prerender', 'headless', 'phantom', 'scraper', 
      'selenium', 'puppeteer', 'lighthouse', 'chrome-lighthouse', 'pingdom', 'gtmetrix',
      'pagespeed', 'insights', 'yandex', 'googlebot', 'bingbot', 'baiduspider', 'duckduckbot',
      'facebookexternalhit', 'whatsapp', 'twitterbot', 'linkedinbot', 'discordbot',
      'telegrambot', 'snapchat', 'pinterest', 'screaming', 'ahrefsbot', 'semrushbot'
    ];

    // Check if user agent contains any bot pattern
    const isBot = botPatterns.some(pattern => userAgent.includes(pattern));
    
    // Log in development but not in production
    if (process.env.NODE_ENV !== 'production' && isBot) {
      console.log(`🤖 Bot detected: ${userAgent}`);
    }

    return isBot;
  }

  /**
   * Initialize the analytics service
   * @param {Object} options - Configuration options
   * @param {string} options.userId - Current user ID (if authenticated)
   * @param {Object} options.userProperties - Additional user properties
   * @param {boolean} options.enableMixpanel - Whether to enable Mixpanel tracking
   * @param {boolean} options.enableInternal - Whether to enable internal tracking
   */
  init(options = {}) {
    const { 
      userId = null,
      userProperties = {},
      enableMixpanel = true,
      enableInternal = true,
    } = options;

    // Store configuration
    this.userId = userId;
    this.userProperties = userProperties;
    this.enableMixpanel = enableMixpanel && !this.isBot; // Disable Mixpanel for bots
    this.enableInternal = enableInternal;

    // Generate anonymous ID if user is not logged in
    this.anonymousId = this.getAnonymousId();

    // Initialize Mixpanel if enabled and not a bot
    if (this.enableMixpanel && MIXPANEL_TOKEN && !this.isBot) {
      try {
        mixpanel.init(MIXPANEL_TOKEN, { 
          debug: process.env.NODE_ENV !== 'production',
          track_pageview: true,
          persistence: 'localStorage'
        });
        
        // Identify user if we have a userId, otherwise use anonymous ID
        if (this.userId) {
          mixpanel.identify(this.userId);
          
          // Register super properties that will be sent with all events
          mixpanel.register({
            'Platform': 'Web',
            'App Version': process.env.REACT_APP_VERSION || '1.0.0',
          });
          
          // Set user properties in Mixpanel
          if (Object.keys(this.userProperties).length > 0) {
            mixpanel.people.set({
              ...this.userProperties,
              '$last_login': new Date(),
            });
          }
        } else {
          // Use anonymous ID for users who aren't signed in
          mixpanel.identify(this.anonymousId);
          
          // Register minimal properties
          mixpanel.register({
            'Platform': 'Web',
            'App Version': process.env.REACT_APP_VERSION || '1.0.0',
            'User Type': 'Anonymous'
          });
        }
      } catch (error) {
        console.error('Failed to initialize Mixpanel:', error);
      }
    } else if (this.isBot && process.env.NODE_ENV !== 'production') {
      console.log('📊 Mixpanel tracking disabled for bot/crawler');
    }

    this.initialized = true;

    // Process any queued events
    if (this.queuedEvents.length > 0) {
      this.queuedEvents.forEach(event => {
        this.track(event.name, event.properties, event.options);
      });
      this.queuedEvents = [];
    }
  }

  /**
   * Get or create an anonymous ID for tracking unregistered users
   * @returns {string} Anonymous ID
   */
  getAnonymousId() {
    // Try to get existing anonymous ID from localStorage
    let anonymousId = localStorage.getItem('anonymousId');
    
    // If not found, create a new one
    if (!anonymousId) {
      anonymousId = 'anon_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('anonymousId', anonymousId);
    }
    
    return anonymousId;
  }

  /**
   * Update the current user ID and properties
   * @param {string} userId - User ID to set
   * @param {Object} userProperties - User properties to update
   */
  setUser(userId, userProperties = {}) {
    // Store the previous anonymous ID if it exists
    const prevAnonymousId = this.anonymousId;
    
    this.userId = userId;
    this.userProperties = { ...this.userProperties, ...userProperties };

    if (this.enableMixpanel && MIXPANEL_TOKEN) {
      try {
        // Get the current distinct_id before identifying
        const currentDistinctId = mixpanel.get_distinct_id();
        
        // Identify the user with their actual ID
        mixpanel.identify(userId);
        
        // Set user properties
        if (Object.keys(userProperties).length > 0) {
          mixpanel.people.set({
            ...userProperties,
            '$last_login': new Date(),
          });
        }
        
        // Handle aliasing to prevent duplicate users:
        // 1. If we have a previous anonymous ID that differs from the user ID, alias them
        // 2. If the current distinct_id differs from the user ID, alias them
        // This covers both the case of previous site usage and direct-to-login
        if (prevAnonymousId && prevAnonymousId !== userId) {
          console.log(`Aliasing anonymous user ${prevAnonymousId} with authenticated user ${userId}`);
          mixpanel.alias(userId, prevAnonymousId);
        } else if (currentDistinctId && currentDistinctId !== userId) {
          console.log(`Aliasing current user ${currentDistinctId} with authenticated user ${userId}`);
          mixpanel.alias(userId, currentDistinctId);
        }
      } catch (error) {
        console.error('Error updating user in Mixpanel:', error);
      }
    }
    
    // Log the user identification in development mode
    if (process.env.NODE_ENV !== 'production') {
      console.log(`👤 User identified: ${userId}`);
    }
  }

  /**
   * Clear the current user (for logout)
   */
  clearUser() {
    this.userId = null;
    this.userProperties = {};

    if (this.enableMixpanel && MIXPANEL_TOKEN) {
      mixpanel.reset();
    }
  }

  /**
   * Track an analytics event
   * @param {string} eventName - Name of the event to track
   * @param {Object} properties - Event properties
   * @param {Object} options - Tracking options
   * @param {boolean} options.mixpanel - Whether to track in Mixpanel
   * @param {boolean} options.internal - Whether to track in internal system
   * @returns {Promise<void>}
   */
  async track(eventName, properties = {}, options = {}) {
    // If not initialized, queue the event for later
    if (!this.initialized) {
      this.queuedEvents.push({ name: eventName, properties, options });
      return;
    }

    const { 
      mixpanel: trackMixpanel = this.enableMixpanel, 
      internal: trackInternal = this.enableInternal 
    } = options;

    // Add common properties
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      userId: this.userId,
      anonymousId: this.anonymousId,
      isAuthenticated: !!this.userId,
      platform: 'web',
      pathname: window.location.pathname,
      url: window.location.href,
      isBot: this.isBot, // Add bot detection to events
    };

    // Log the event in development mode
    if (process.env.NODE_ENV !== 'production') {
      console.log(`📊 Analytics event: ${eventName}`, enrichedProperties);
    }

    // Track in Mixpanel (skip if bot detected)
    if (trackMixpanel && MIXPANEL_TOKEN && !this.isBot) {
      try {
        mixpanel.track(eventName, enrichedProperties);
      } catch (error) {
        console.error(`Failed to track event "${eventName}" in Mixpanel:`, error);
      }
    }

    // Track in internal system only if user is signed in
    // We still send internal tracking for bots for server-side analysis if needed
    if (trackInternal && API_URL && this.userId) {
      try {
        await axios.post(`${API_URL}analytics/track`, {
          name: eventName,
          properties: enrichedProperties
        }, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY
          }
        });
      } catch (error) {
        console.error(`Failed to track event "${eventName}" in internal system:`, error);
      }
    }
  }

  /**
   * Track a page view event
   * @param {Object} properties - Additional properties
   */
  trackPageView(properties = {}) {
    const pageProperties = {
      path: window.location.pathname,
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
      ...properties
    };

    this.track('Page View', pageProperties);
  }

  /**
   * Track a user interaction event
   * @param {string} action - The action performed (click, submit, etc.)
   * @param {string} category - The category of the interaction (button, form, etc.)
   * @param {string} label - Label for the interaction
   * @param {Object} properties - Additional properties
   */
  trackInteraction(action, category, label, properties = {}) {
    this.track('User Interaction', {
      action,
      category,
      label,
      ...properties
    });
  }

  /**
   * Track an error event
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   * @param {Object} context - Additional context
   */
  trackError(errorType, errorMessage, context = {}) {
    this.track('Error', {
      errorType,
      errorMessage,
      ...context
    });
  }

  /**
   * Track a form submission
   * @param {string} formName - Name of the form
   * @param {boolean} success - Whether submission was successful
   * @param {Object} formData - Form data (sanitized)
   */
  trackFormSubmission(formName, success, formData = {}) {
    // Remove sensitive fields from the tracking data
    const sanitizedData = { ...formData };
    const sensitiveFields = ['password', 'creditCard', 'ssn', 'token'];
    
    sensitiveFields.forEach(field => {
      if (sanitizedData[field]) {
        sanitizedData[field] = '[REDACTED]';
      }
    });

    this.track('Form Submission', {
      formName,
      success,
      formData: sanitizedData
    });
  }

  /**
   * Track an event related to the recommendation system
   * This specifically targets the internal tracking system
   * @param {string} action - Recommendation action (view, click, ignore, etc.)
   * @param {string} itemId - ID of the recommended item
   * @param {string} itemType - Type of item (event, organization, etc.)
   * @param {Object} properties - Additional properties
   */
  trackRecommendation(action, itemId, itemType, properties = {}) {
    this.track('Recommendation', {
      recommendationAction: action,
      itemId,
      itemType,
      eventId: itemType === 'event' ? itemId : undefined, // Map to eventId for backend compatibility
      ...properties
    }, { mixpanel: true, internal: true });
  }

  /**
   * Track a view of an event page
   * @param {string|number} eventId - ID of the event
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Additional properties
   */
  trackEventView(eventId, eventName, properties = {}) {
    // Ensure eventId is treated as a number or string (not object)
    const normalizedEventId = typeof eventId === 'object' ? String(eventId.event_id || eventId.id || eventId._id) : eventId;
    
    console.log(`📊 Tracking event view: ${normalizedEventId} - ${eventName}`);
    
    this.track('Event View', {
      eventId: normalizedEventId, // Explicitly set eventId for the backend
      eventName,
      ...properties
    }, { mixpanel: true, internal: true });
  }

  /**
   * Track a free event signup
   * @param {string} eventId - ID of the event
   * @param {string} eventName - Name of the event
   * @param {Object} ticketInfo - Information about the ticket
   * @param {Object} properties - Additional properties
   */
  trackFreeEventSignup(eventId, eventName, ticketInfo = {}, properties = {}) {
    // Ensure eventId is treated as a number or string
    const normalizedEventId = typeof eventId === 'object' ? String(eventId.event_id || eventId.id || eventId._id) : eventId;
    
    this.track('Free Event Signup', {
      eventId: normalizedEventId,
      eventName,
      ticketInfo,
      ...properties
    }, { mixpanel: true, internal: true });
  }

  /**
   * Track a paid event ticket purchase
   * @param {string} eventId - ID of the event
   * @param {string} eventName - Name of the event
   * @param {Object} ticketInfo - Information about the ticket
   * @param {number} amount - Purchase amount
   * @param {string} currency - Currency code
   * @param {Object} properties - Additional properties
   */
  trackTicketPurchase(eventId, eventName, ticketInfo = {}, amount, currency = 'USD', properties = {}) {
    this.track('Ticket Purchase', {
      eventId,
      eventName,
      ticketInfo,
      amount,
      currency,
      ...properties
    }, { mixpanel: true, internal: true });
    
    // Also track as a revenue event in Mixpanel (skip if bot detected)
    if (this.enableMixpanel && MIXPANEL_TOKEN && amount && !this.isBot) {
      try {
        mixpanel.track_charge(amount, {
          $transaction_id: properties.transactionId || `${eventId}-${Date.now()}`,
          eventId,
          eventName,
          ...ticketInfo
        });
      } catch (error) {
        console.error('Failed to track revenue in Mixpanel:', error);
      }
    }
  }

  /**
   * Track when a user creates an organization
   * @param {string} orgId - ID of the organization
   * @param {string} orgName - Name of the organization
   * @param {Object} orgInfo - Additional organization information
   * @param {Object} properties - Additional properties
   */
  trackOrganizationCreation(orgId, orgName, orgInfo = {}, properties = {}) {
    this.track('Organization Created', {
      orgId,
      orgName,
      orgInfo,
      ...properties
    }, { mixpanel: true, internal: false });
  }

  /**
   * Track when a user creates an event
   * @param {string} eventId - ID of the event
   * @param {string} eventName - Name of the event
   * @param {string} orgId - ID of the organization
   * @param {Object} eventInfo - Additional event information
   * @param {Object} properties - Additional properties
   */
  trackEventCreation(eventId, eventName, orgId, eventInfo = {}, properties = {}) {
    this.track('Event Created', {
      eventId,
      eventName,
      organizationId: orgId,
      eventInfo,
      ...properties
    }, { mixpanel: true, internal: false });
  }

  /**
   * Track when a user shares an event
   * @param {string} eventId - ID of the event
   * @param {string} eventName - Name of the event
   * @param {string} shareMethod - Method of sharing (social, email, copy link, etc.)
   * @param {Object} properties - Additional properties
   */
  trackEventShare(eventId, eventName, shareMethod, properties = {}) {
    this.track('Event Shared', {
      eventId,
      eventName,
      shareMethod,
      ...properties
    }, { mixpanel: true, internal: true });
  }

  /**
   * Track when a user clicks a button on the home page
   * @param {string} buttonId - ID or name of the button
   * @param {string} buttonText - Text shown on the button
   * @param {string} section - Section of the home page where the button is located
   * @param {Object} properties - Additional properties
   */
  trackHomeButtonClick(buttonId, buttonText, section, properties = {}) {
    this.track('Home Button Click', {
      buttonId,
      buttonText,
      section,
      ...properties
    }, { mixpanel: true, internal: false });
  }

  /**
   * Track user registration for an event (works for both free and paid)
   * @param {string} eventId - ID of the event
   * @param {string} eventName - Name of the event
   * @param {string} registrationType - Type of registration (free, paid)
   * @param {Object} ticketInfo - Information about the ticket
   * @param {Object} properties - Additional properties
   */
  trackEventRegistration(eventId, eventName, registrationType, ticketInfo = {}, properties = {}) {
    // Ensure eventId is treated as a number or string
    const normalizedEventId = typeof eventId === 'object' ? String(eventId.event_id || eventId.id || eventId._id) : eventId;
    
    console.log(`📊 Tracking event registration: ${normalizedEventId} - ${eventName} (${registrationType})`);
    
    this.track('Event Registration', {
      eventId: normalizedEventId,
      eventName,
      registrationType,
      ticketInfo,
      ...properties
    }, { mixpanel: true, internal: true });
  }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;

// Export individual tracking functions for convenience
export const trackEvent = (name, properties, options) => analyticsService.track.bind(analyticsService)(name, properties, options);
export const trackPageView = (properties) => analyticsService.trackPageView(properties);
export const trackInteraction = (action, category, label, properties) => analyticsService.trackInteraction(action, category, label, properties);
export const trackError = (errorType, errorMessage, context) => analyticsService.trackError(errorType, errorMessage, context);
export const trackFormSubmission = (formName, success, formData) => analyticsService.trackFormSubmission(formName, success, formData);
export const trackRecommendation = (action, itemId, itemType, properties) => analyticsService.trackRecommendation(action, itemId, itemType, properties);
export const trackFreeEventSignup = (eventId, eventName, ticketInfo, properties) => analyticsService.trackFreeEventSignup(eventId, eventName, ticketInfo, properties);
export const trackTicketPurchase = (eventId, eventName, ticketInfo, amount, currency, properties) => analyticsService.trackTicketPurchase(eventId, eventName, ticketInfo, amount, currency, properties);
export const trackOrganizationCreation = (orgId, orgName, orgInfo, properties) => analyticsService.trackOrganizationCreation(orgId, orgName, orgInfo, properties);
export const trackEventCreation = (eventId, eventName, orgId, eventInfo, properties) => analyticsService.trackEventCreation(eventId, eventName, orgId, eventInfo, properties);
export const trackEventShare = (eventId, eventName, shareMethod, properties) => analyticsService.trackEventShare(eventId, eventName, shareMethod, properties);
export const trackHomeButtonClick = (buttonId, buttonText, section, properties) => analyticsService.trackHomeButtonClick(buttonId, buttonText, section, properties);
export const trackEventRegistration = (eventId, eventName, registrationType, ticketInfo, properties) => analyticsService.trackEventRegistration(eventId, eventName, registrationType, ticketInfo, properties); 